import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@sainsburys-tech/fable'
import { Offer, ChevronRight } from '@sainsburys-tech/icons'
import { InView } from 'react-intersection-observer'
import { pushEventAndTrack } from '@/javascript/helpers/taggingSerializer'

const TradeIn = ({ heading, subtext, useWidget = true }) => {
  const [isTradeInScriptLoaded, setTradeInScriptLoaded] = useState(false)
  const createdElementsRef = useRef([])

  const trackEvent = (eventAction, eventName) => {
    pushEventAndTrack({
      eventInfo: {
        eventAction,
        eventName
      }
    })
  }

  useEffect(() => {
    trackEvent('trade-in panel:content loaded', 'trade-in panel')

    // Cleanup function to remove created elements and their handlers
    return () => {
      createdElementsRef.current.forEach(element => {
        if (element && element.tagName === 'SCRIPT') {
          element.onload = null
          element.onerror = null
        }
        // Remove element from DOM
        if (element && element.parentNode) {
          element.parentNode.removeChild(element)
        }
      })
      createdElementsRef.current = []
    }
  }, [])

  const handleInView = inView => {
    if (inView) {
      trackEvent('trade-in panel:content viewed', 'trade-in panel')
    }
  }

  const loadTradeInScripts = () => {
    if (isTradeInScriptLoaded) return Promise.resolve()

    return new Promise(resolve => {
      const linkElement = document.createElement('link')
      linkElement.rel = 'stylesheet'
      linkElement.href = '/product-assets/static/tradein-widget/styles.20251120.css'
      document.head.appendChild(linkElement)
      createdElementsRef.current.push(linkElement)

      const mainScript = document.createElement('script')
      mainScript.src = '/product-assets/static/tradein-widget/main.20251120.js'
      mainScript.defer = true

      const handleLoad = () => {
        // Clean up event handlers immediately after use
        mainScript.onload = null
        mainScript.onerror = null
        setTradeInScriptLoaded(true)
        setTimeout(resolve, 200)
      }

      const handleError = () => {
        // Clean up event handlers immediately after use
        mainScript.onload = null
        mainScript.onerror = null
        console.error('Failed to load trade-in scripts')
        resolve()
      }

      mainScript.onload = handleLoad
      mainScript.onerror = handleError

      document.head.appendChild(mainScript)
      createdElementsRef.current.push(mainScript)
    })
  }

  const handleClick = async event => {
    event.preventDefault()

    const existingWidget = document.querySelector('argos-trade-in-widget')
    if (existingWidget) {
      return
    }

    trackEvent('trade-in panel:cta clicked', 'trade-in panel')

    try {
      await loadTradeInScripts()
      if (
        typeof window !== 'undefined' &&
        window.ArgosTradeInWidget &&
        typeof window.ArgosTradeInWidget.init === 'function'
      ) {
        window.ArgosTradeInWidget.init()
      }
    } catch (error) {
      window.open('https://www.argos-tradein.co.uk/', '_blank', 'noopener,noreferrer')
    }
  }

  const handleAnchorClick = () => {
    trackEvent('trade-in panel:cta clicked', 'trade-in panel')
  }

  const content = (
    <Box className='trade-in-container' as='div' border={{ radius: 'lg', size: 'sm' }}>
      <div className='ds-flex ds-items-center ds-gap-1 ds-text-green-600 ds-font-semibold ds-text-sm'>
        <Offer className='offer-icon' />
        <span>{heading}</span>
        <ChevronRight className='chevron-icon-right' />
      </div>
      <div className='ds-mx-[1.75rem] ds-text-black ds-text-sm ds-font-normal'>{subtext}</div>
    </Box>
  )

  const wrapper = useWidget ? (
    <>
      <button className='trade-in-button' type='button' onClick={handleClick}>
        {content}
      </button>
      <div id='argos-trade-in-widget-container' />
    </>
  ) : (
    <a
      href='https://www.argos-tradein.co.uk/'
      target='_blank'
      rel='noopener noreferrer'
      style={{ textDecoration: 'none' }}
      onClick={handleAnchorClick}
    >
      {content}
    </a>
  )

  return (
    <InView triggerOnce as='div' onChange={handleInView} threshold={0.5}>
      <div className='top-border'>{wrapper}</div>
    </InView>
  )
}

TradeIn.propTypes = {
  heading: PropTypes.string.isRequired,
  subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  useWidget: PropTypes.bool
}

export default TradeIn
